
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import {
  Layers, Eye, Mountain, Coffee, Tent, Lock, Zap, Flame, Anchor,
  Route, Loader2, Snowflake, Caravan, Sparkles, Bird, Download, LandPlot,
  CloudRain, X, Droplet, Camera, Check, ChevronRight, ChevronDown, ChevronUp, ArrowLeft, Star, Construction, Fish,
  AlertTriangle, Trash2, SquareParking, Info, Waves, Landmark, TreePine, Tractor, Orbit, ShieldAlert, Users,
  Activity, Scale, Wrench, Smile, Footprints, Bike,
} from 'lucide-react';
import { usePremiumNudge } from '../../contexts/PremiumNudgeContext';
import { hasTierAccess } from '../../services/accessControl';
import type { PremiumFeatureKey } from '../PremiumNudge';
import { isLayerSupportedInCountry, type SupportedCountry } from '../../utils/countryDetection';

const isIOS = Capacitor.getPlatform() === 'ios';

type LayerTab = 'basemap' | 'overlays';

const BASEMAP_THUMBNAILS = {
  topo: 'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/10/295/537.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/10/295/537',
} as const;

// --- Overlay layer metadata ---

type OverlayLayerKey =
  | 'precipitationRadar' | 'hiking' | 'winterRoads' | 'mountainRoads'
  | 'barriers' | 'propertyBoundaries' | 'chargers' | 'webcams'
  | 'dumpStations' | 'restAreas' | 'coastal'
  | 'algorithmic' | 'wildlife' | 'fishing' | 'commercialCampsites' | 'commercialRvParks' | 'osmAmenities'
  | 'wasteStations' | 'bathingPlaces' | 'discoverAttractions' | 'forestRoads' | 'tractorRoads' | 'serviceRoads'
  | 'svvVegnett' | 'svvVegdekke' | 'svvHoyde' | 'svvTrafikk' | 'trafficEvents' | 'playgrounds'
  | 'parkingDk';

interface LayerMeta {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  iconColor: string;
  label: string;
  previewTileUrl: string;
  premiumFeatureKey?: PremiumFeatureKey;
  subtitle?: string;
  hideOnIOS?: boolean;
  countrySupport: readonly string[]; // ['*'] = global, ['NO'] = Norway only
}

const TOPO_PREVIEW = BASEMAP_THUMBNAILS.topo;

// Per-type sub-toggles nested under the "Turstier (GeoNorge)" row. Colours mirror
// Kartverket's own route styling so the menu legend matches the map.
type HikingSubKey = 'hikingFoot' | 'hikingSki' | 'hikingBike' | 'hikingOther';
const HIKING_SUBTYPES: { key: HikingSubKey; icon: React.ComponentType<{ size?: number; className?: string }>; iconColor: string; labelKey: string; fallback: string }[] = [
  { key: 'hikingFoot', icon: Footprints, iconColor: 'text-red-500', labelKey: 'map.layers.hiking_foot', fallback: 'Fottur' },
  { key: 'hikingSki', icon: Snowflake, iconColor: 'text-sky-500', labelKey: 'map.layers.hiking_ski', fallback: 'Skiløype' },
  { key: 'hikingBike', icon: Bike, iconColor: 'text-emerald-600', labelKey: 'map.layers.hiking_bike', fallback: 'Sykkeltur' },
  { key: 'hikingOther', icon: Route, iconColor: 'text-violet-500', labelKey: 'map.layers.hiking_other', fallback: 'Annen rute' },
];

const LAYER_METADATA: Record<OverlayLayerKey, LayerMeta> = {
  precipitationRadar: {
    icon: CloudRain, iconColor: 'text-cyan-500',
    label: 'Nedbørsradar (animert)',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'precipitation_radar',
    hideOnIOS: true,
    countrySupport: ['*'],
  },
  hiking: {
    icon: Route, iconColor: 'text-red-500',
    label: 'Turstier (GeoNorge)',
    previewTileUrl: 'https://wms.geonorge.no/skwms1/wms.friluftsruter2?SERVICE=WMS&REQUEST=GetMap&LAYERS=Ruter&FORMAT=image/png&TRANSPARENT=true&VERSION=1.3.0&WIDTH=256&HEIGHT=256&CRS=EPSG:3857&BBOX=600000,8300000,900000,8600000',
    premiumFeatureKey: 'hiking_trails',
    countrySupport: ['NO'],
  },
  winterRoads: {
    icon: Snowflake, iconColor: 'text-cyan-500',
    label: 'Vinterstengte veier',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO'],
  },
  mountainRoads: {
    icon: Mountain, iconColor: 'text-purple-600',
    label: 'Fjellveier / Anleggsveier',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  barriers: {
    icon: Construction, iconColor: 'text-red-500',
    label: 'Bommer / Sperringer',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  propertyBoundaries: {
    icon: LandPlot, iconColor: 'text-amber-600',
    label: 'Eiendomsgrenser',
    previewTileUrl: 'https://wms.geonorge.no/skwms1/wms.matrikkelkart?SERVICE=WMS&REQUEST=GetMap&LAYERS=MatrikkelVMS_eiendomsgrense&FORMAT=image/png&TRANSPARENT=true&VERSION=1.3.0&WIDTH=256&HEIGHT=256&CRS=EPSG:3857&BBOX=1180000,8370000,1185000,8375000',
    premiumFeatureKey: 'property_boundaries',
    subtitle: 'Krever innzooming',
    hideOnIOS: true,
    countrySupport: ['NO', 'DK'],
  },
  chargers: {
    icon: Zap, iconColor: 'text-yellow-500',
    label: 'Ladestasjoner (EV)',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  webcams: {
    icon: Camera, iconColor: 'text-cyan-500',
    label: 'Webkameraer (SVV/Trafikverket)',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'webcams_layer',
    countrySupport: ['NO', 'SE'],
  },
  trafficEvents: {
    icon: AlertTriangle, iconColor: 'text-orange-500',
    label: 'Veimeldinger (Trafikverket)',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'traffic_events',
    subtitle: 'Ulykker, veiarbeid og stengsler',
    countrySupport: ['SE'],
  },
  dumpStations: {
    icon: Droplet, iconColor: 'text-violet-500',
    label: 'Tømmestasjoner (Vann/Septik)',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'dump_stations',
    hideOnIOS: true,
    countrySupport: ['*'],
  },
  wasteStations: {
    icon: Trash2, iconColor: 'text-emerald-600',
    label: 'Avfallshåndtering',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  // GeoDanmark Parkering, 45 516 flater over 200 m². Statiske geocellefiler bak
  // Hosting-CDN-en — laget koster ingen Firestore-lesninger.
  parkingDk: {
    icon: SquareParking, iconColor: 'text-sky-500',
    label: 'Parkering (Danmark)',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['DK'],
  },
  bathingPlaces: {
    icon: Waves, iconColor: 'text-cyan-500',
    label: 'Badeplasser',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'bathing_places',
    countrySupport: ['NO', 'SE', 'DK'],
  },
  restAreas: {
    icon: Coffee, iconColor: 'text-blue-500',
    label: 'Rasteplasser',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO', 'SE', 'DK', 'FI'],
  },
  coastal: {
    icon: Anchor, iconColor: 'text-blue-600',
    label: 'Havner & Brygger',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'coastal_layer',
    countrySupport: ['NO', 'SE', 'DK'],
  },
  algorithmic: {
    icon: Sparkles, iconColor: 'text-emerald-600',
    label: 'Fricamp (AI)',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO', 'SE', 'DK', 'FI'],
  },
  wildlife: {
    icon: Bird, iconColor: 'text-violet-600',
    label: 'Dyreliv',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'wildlife_layer',
    countrySupport: ['NO', 'SE', 'DK'],
  },
  fishing: {
    icon: Fish, iconColor: 'text-blue-500',
    label: 'Fiskemuligheter',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'fishing_layer',
    countrySupport: ['NO', 'SE', 'DK'],
  },
  commercialCampsites: {
    icon: Tent, iconColor: 'text-blue-600',
    label: 'Campingplasser',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  commercialRvParks: {
    icon: Caravan, iconColor: 'text-blue-500',
    label: 'Bobilplasser',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  osmAmenities: {
    icon: Tent, iconColor: 'text-emerald-500',
    label: 'Picnic, Gapahuk, Bål & Utkikk',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  discoverAttractions: {
    icon: Landmark, iconColor: 'text-violet-500',
    label: 'Severdigheter & Kulturminner',
    previewTileUrl: TOPO_PREVIEW,
    premiumFeatureKey: 'discover_attractions',
    countrySupport: ['NO', 'SE', 'DK'],
  },
  playgrounds: {
    icon: Smile, iconColor: 'text-amber-500',
    label: 'Lekeplasser',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
  },
  forestRoads: {
    icon: TreePine, iconColor: 'text-amber-700',
    label: 'Skogsbilveger',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO', 'SE'],
  },
  tractorRoads: {
    icon: Tractor, iconColor: 'text-yellow-700',
    label: 'Traktorveger & Stier',
    previewTileUrl: TOPO_PREVIEW,
    // DK lagt til 23.08.2026 ved lanseringen av Danmark. Danske markvei-plasser
    // får `roadAccessClass: 'tractor_road'`, og App.tsx skjuler dem når laget er
    // av — men laget ble ikke TILBUDT i dansk utsnitt, så 6 264 plasser var
    // ulåselige: ingen bryter fantes å slå på. Enten må laget tilbys, eller
    // gatingen droppes for DK; å gate på en bryter brukeren ikke har er ingen av
    // delene.
    countrySupport: ['NO', 'SE', 'DK'],
  },
  serviceRoads: {
    icon: Wrench, iconColor: 'text-orange-500',
    label: 'Serviceveier',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['*'],
    subtitle: 'Skjuler/viser Fricamp-punkter på servicevei',
  },
  svvVegnett: {
    icon: Route, iconColor: 'text-slate-500',
    label: 'Vegnett (NVDB)',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO'],
    subtitle: 'Alle veger og gater',
    premiumFeatureKey: 'svv_road_data',
  },
  svvVegdekke: {
    icon: Layers, iconColor: 'text-stone-500',
    label: 'Vegdekke (Fast/Løst)',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO'],
    subtitle: 'Asfalt vs. Grus',
    premiumFeatureKey: 'svv_road_data',
  },
  svvHoyde: {
    icon: Scale, iconColor: 'text-red-600',
    label: 'Høydebegrensning',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO'],
    subtitle: 'Underganger/Tunneler',
    premiumFeatureKey: 'svv_road_data',
  },
  svvTrafikk: {
    icon: Activity, iconColor: 'text-blue-600',
    label: 'Trafikkmengde',
    previewTileUrl: TOPO_PREVIEW,
    countrySupport: ['NO'],
    subtitle: 'Årsdøgntrafikk (ÅDT)',
    premiumFeatureKey: 'svv_traffic_data',
  },
};

// Mapping from overlay layer key → stats feed layer key (per country)
const STATS_KEY_MAP: Partial<Record<OverlayLayerKey, string>> = {
  bathingPlaces: 'bathingPlaces',
  restAreas: 'restAreas',
  barriers: 'barriers',
  winterRoads: 'winterRoads',
  chargers: 'chargers',
  webcams: 'webcams',
  dumpStations: 'dumpStations',
  coastal: 'coastalPoints',
  hiking: 'hikingTrails',
  wildlife: 'speciesObservations',
  fishing: 'speciesObservations',
  commercialCampsites: 'campsites',
  commercialRvParks: 'campsites',
  algorithmic: 'spots',
};

const ESSENTIAL_LAYERS: OverlayLayerKey[] = [
  'barriers', 'chargers', 'restAreas', 'commercialCampsites', 'commercialRvParks', 'osmAmenities', 'hiking'
];

const ADVANCED_SECTIONS: { titleKey: string; title: string; layers: OverlayLayerKey[] }[] = [
  { titleKey: 'weather', title: 'Vær & Radar', layers: ['precipitationRadar'] },
  { titleKey: 'roads', title: 'Veinett & Status', layers: ['forestRoads', 'tractorRoads', 'serviceRoads', 'svvVegnett', 'svvVegdekke', 'svvHoyde', 'svvTrafikk', 'winterRoads', 'mountainRoads', 'propertyBoundaries'] },
  { titleKey: 'poi', title: 'Interessepunkter', layers: ['webcams', 'dumpStations', 'wasteStations', 'bathingPlaces', 'coastal', 'discoverAttractions', 'playgrounds', 'parkingDk'] },
  { titleKey: 'nature', title: 'Natur & Analyse', layers: ['algorithmic', 'wildlife', 'fishing'] },
];

// Keep OVERLAY_SECTIONS for backwards compatibility with any external references
const OVERLAY_SECTIONS: { titleKey: string; title: string; layers: OverlayLayerKey[] }[] = [
  { titleKey: 'weather', title: 'Vær & Radar', layers: ['precipitationRadar'] },
  { titleKey: 'trails', title: 'Tur & Stier', layers: ['hiking'] },
  { titleKey: 'roads', title: 'Veinett & Status', layers: ['forestRoads', 'tractorRoads', 'serviceRoads', 'winterRoads', 'mountainRoads', 'barriers', 'propertyBoundaries'] },
  { titleKey: 'poi', title: 'Interessepunkter', layers: ['chargers', 'webcams', 'dumpStations', 'wasteStations', 'bathingPlaces', 'restAreas', 'coastal', 'discoverAttractions', 'playgrounds', 'parkingDk'] },
  { titleKey: 'nature', title: 'Overnatting & Natur', layers: ['algorithmic', 'wildlife', 'fishing', 'commercialCampsites', 'osmAmenities'] },
];

// --- Component ---

interface MapLayersControlProps {
  layers: {
    topo: boolean;
    satellite: boolean;
    protected: boolean;
    warnings: boolean;
    hiking: boolean;
    hikingFoot: boolean;
    hikingSki: boolean;
    hikingBike: boolean;
    hikingOther: boolean;
    restAreas: boolean;
    osmAmenities: boolean;
    commercialCampsites: boolean;
    commercialRvParks: boolean;
    barriers: boolean;
    chargers: boolean;
    dumpStations: boolean;
    wasteStations: boolean;
    parkingDk?: boolean;
    bathingPlaces: boolean;
    coastal: boolean;
    discoverAttractions: boolean;
    mountainRoads: boolean;
    gpxRoutes: boolean;
    winterRoads: boolean;
    algorithmic: boolean;
    wildlife: boolean;
    fishing: boolean;
    vehicleFiltered: boolean;
    propertyBoundaries: boolean;
    precipitationRadar: boolean;
    hillshade: boolean;
    webcams: boolean;
    forestRoads: boolean;
    tractorRoads: boolean;
    serviceRoads: boolean;
    svvVegnett: boolean;
    svvVegdekke: boolean;
    svvHoyde: boolean;
    svvTrafikk: boolean;
    playgrounds: boolean;
  };
  onUpdateLayers: (layers: MapLayersControlProps['layers']) => void;
  loadingState?: {
    restAreas?: boolean;
    osmAmenities?: boolean;
    commercialCampsites?: boolean;
    barriers?: boolean;
    chargers?: boolean;
    dumpStations?: boolean;
    wasteStations?: boolean;
    parkingDk?: boolean;
    bathingPlaces?: boolean;
    coastal?: boolean;
    mountainRoads?: boolean;
    winterRoads?: boolean;
    wildlife?: boolean;
    fishing?: boolean;
    webcams?: boolean;
    playgrounds?: boolean;
  };
  errorState?: Partial<Record<string, boolean>>;
  counts?: {
    restAreas?: number;
    commercialCampsites?: number;
    osmAmenities?: number;
    coastal?: number;
    barriers?: number;
    chargers?: number;
    dumpStations?: number;
    wasteStations?: number;
    parkingDk?: number;
    bathingPlaces?: number;
    mountainRoads?: number;
    winterRoads?: number;
    wildlife?: number;
    fishing?: number;
    webcams?: number;
    playgrounds?: number;
  };
  isPaidUser?: boolean;
  isPremiumUser?: boolean;
  userTier?: number;
  onOpenUpgrade?: () => void;
  onOpenOfflineManager?: () => void;
  viewportCountry?: SupportedCountry | null;
  activeVehicleLength?: number | null;
  is3DMode?: boolean;
  onToggle3D?: () => void;
  vehicleWarningLayers?: Set<string>;
  onVehicleWarning?: (layerKey: string) => void;
  showLayerTip?: boolean;
  onDismissLayerTip?: () => void;
}

const MapLayersControl: React.FC<MapLayersControlProps> = ({
  layers, onUpdateLayers, loadingState, errorState, counts = {} as NonNullable<MapLayersControlProps['counts']>,
  isPaidUser = false, isPremiumUser = false, userTier = 0, onOpenUpgrade, onOpenOfflineManager, viewportCountry, activeVehicleLength,
  is3DMode = false, onToggle3D, vehicleWarningLayers, onVehicleWarning, showLayerTip = false, onDismissLayerTip,
}) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<LayerTab>('basemap');
  const [detailLayer, setDetailLayer] = useState<OverlayLayerKey | null>(null);
  const showNudge = usePremiumNudge();
  const [layerStats, setLayerStats] = useState<Record<string, any> | null>(null);
  const [showOfflineBtn, setShowOfflineBtn] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hikingExpanded, setHikingExpanded] = useState(false);

  // Master "Turstier" reflects the per-type sub-toggles; toggling it flips all four.
  const anyHikingOn = layers.hikingFoot || layers.hikingSki || layers.hikingBike || layers.hikingOther;
  const setHikingType = (key: HikingSubKey, val: boolean) => {
    const next = { ...layers, [key]: val };
    next.hiking = next.hikingFoot || next.hikingSki || next.hikingBike || next.hikingOther;
    onUpdateLayers(next);
  };
  const setAllHiking = (val: boolean) => {
    onUpdateLayers({ ...layers, hiking: val, hikingFoot: val, hikingSki: val, hikingBike: val, hikingOther: val });
  };

  useEffect(() => {
    if (showMenu) {
      setActiveTab('basemap');
      setDetailLayer(null);
      setShowOfflineBtn(true);
      const timer = setTimeout(() => setShowOfflineBtn(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showMenu]);

  useEffect(() => {
    if (!showLayerTip) return;
    const timer = window.setTimeout(() => onDismissLayerTip?.(), 3000);
    return () => window.clearTimeout(timer);
  }, [showLayerTip, onDismissLayerTip]);

  // Fetch layer stats once (cached API, fast response)
  useEffect(() => {
    if (!detailLayer || layerStats) return;
    fetch('/api/stats').then(r => r.json()).then(setLayerStats).catch(() => {});
  }, [detailLayer, layerStats]);

  const hasSatelliteAccess = isPaidUser || hasTierAccess({ tier: userTier }, 'satellite_map');

  const hasLayerAccess = useCallback((layerKey: OverlayLayerKey): boolean => {
    const meta = LAYER_METADATA[layerKey];
    if (!meta.premiumFeatureKey) return true;
    
    const isAdmin = userTier >= 10 || isPaidUser; // Generic paid check for tier access
    
    // Specifc check for Premium-only features
    if (meta.premiumFeatureKey === 'svv_traffic_data') {
      return isPremiumUser || userTier >= 5; // Premium or highest Legend tier
    }

    return isPaidUser || hasTierAccess({ tier: userTier }, meta.premiumFeatureKey);
  }, [isPaidUser, isPremiumUser, userTier]);

  const LoadingSpinner = () => (
    <div className="w-5 h-5 flex items-center justify-center">
      <Loader2 size={14} className="animate-spin text-emerald-500" />
    </div>
  );

  // --- LayerRow ---
  const LayerRow = ({ layerKey }: { layerKey: OverlayLayerKey }) => {
    const meta = LAYER_METADATA[layerKey];
    const Icon = meta.icon;
    const isEnabled = layers[layerKey];
    const hasAccess = hasLayerAccess(layerKey);
    const isPremium = !!meta.premiumFeatureKey;
    const count = (counts as Record<string, number | undefined>)[layerKey];
    const isLoading = (loadingState as Record<string, boolean | undefined> | undefined)?.[layerKey];
    const hasError = isEnabled && errorState?.[layerKey];
    const isLimitedInViewport = viewportCountry && !isLayerSupportedInCountry(meta.countrySupport, viewportCountry);

    const handleToggle = (e: React.MouseEvent | React.ChangeEvent) => {
      e.stopPropagation();
      if (!hasAccess && meta.premiumFeatureKey) {
        showNudge(meta.premiumFeatureKey);
        return;
      }
      if (!isEnabled && vehicleWarningLayers?.has(layerKey)) {
        onVehicleWarning?.(layerKey);
      }
      onUpdateLayers({ ...layers, [layerKey]: !isEnabled });
    };

    const renderIcon = () => {
      if (layerKey === 'osmAmenities') {
        return (
          <div className="flex -space-x-1 flex-shrink-0">
            <Tent size={14} className="text-emerald-500" />
            <Flame size={14} className="text-orange-500" />
            <Mountain size={14} className="text-emerald-400" />
          </div>
        );
      }
      if (layerKey === 'bathingPlaces') {
        return <i className="fa-solid fa-person-swimming text-sm text-cyan-500 flex-shrink-0" />;
      }
      return <Icon size={14} className={`${meta.iconColor} flex-shrink-0`} />;
    };

    // "Turstier (GeoNorge)" is a nested group: master toggle + expandable per-type sub-toggles.
    if (layerKey === 'hiking') {
      const blockNudge = () => {
        if (!hasAccess && meta.premiumFeatureKey) { showNudge(meta.premiumFeatureKey); return true; }
        return false;
      };
      return (
        <>
          <div className={`flex items-center justify-between group ${!hasAccess ? 'opacity-80' : ''}`}>
            <div
              className="flex items-center gap-3 text-tiny font-bold text-[var(--text-primary)] flex-1 min-w-0 cursor-pointer"
              onClick={() => setDetailLayer('hiking')}
            >
              <Icon size={14} className={`${meta.iconColor} flex-shrink-0`} />
              <span className="truncate">{meta.label}</span>
              {isLimitedInViewport && (
                <span className="text-4xs font-bold text-sky-500 bg-sky-500/10 px-1.5 py-0.5 rounded-full ml-auto mr-1 flex-shrink-0 whitespace-nowrap">
                  {t('layer_coverage.norway_only_short')}
                </span>
              )}
              {isPremium && !hasAccess && <Lock size={10} className={`text-amber-500 ${isLimitedInViewport ? '' : 'ml-auto'} mr-1 flex-shrink-0`} />}
              {isPremium && hasAccess && <Star size={10} className={`text-emerald-500 ${isLimitedInViewport ? '' : 'ml-auto'} mr-1 flex-shrink-0 fill-emerald-500`} />}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <input
                type="checkbox"
                checked={anyHikingOn}
                onChange={(e) => { e.stopPropagation(); if (blockNudge()) return; setAllHiking(!anyHikingOn); }}
                className="w-5 h-5 accent-emerald-500 cursor-pointer"
              />
              <button
                onClick={() => setHikingExpanded(v => !v)}
                aria-label={t('map.layers.hiking_types', 'Turtyper')}
                className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors p-2"
              >
                {hikingExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            </div>
          </div>
          {hikingExpanded && (
            <div className="ml-7 mt-1.5 space-y-2 border-l border-[var(--border-primary)] pl-3">
              {HIKING_SUBTYPES.map(st => {
                const SubIcon = st.icon;
                const on = layers[st.key];
                return (
                  <div key={st.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 text-tiny font-bold text-[var(--text-primary)] min-w-0">
                      <SubIcon size={13} className={`${st.iconColor} flex-shrink-0`} />
                      <span className="truncate">{t(st.labelKey, st.fallback)}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => { if (blockNudge()) return; setHikingType(st.key, !on); }}
                      className="w-5 h-5 accent-emerald-500 cursor-pointer flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      );
    }

    return (
      <div className={`flex items-center justify-between group ${!hasAccess ? 'opacity-80' : ''}`}>
        <div
          className="flex items-center gap-3 text-tiny font-bold text-[var(--text-primary)] flex-1 min-w-0 cursor-pointer"
          onClick={() => setDetailLayer(layerKey)}
        >
          {renderIcon()}
          {meta.subtitle ? (
            <div className="flex flex-col min-w-0">
              <span className="truncate">{meta.label}</span>
              <span className="text-3xs font-normal tracking-wide text-[var(--text-muted)]">{meta.subtitle}</span>
            </div>
          ) : (
            <span className="truncate">{meta.label}</span>
          )}
          {vehicleWarningLayers?.has(layerKey) && (
            <AlertTriangle size={12} className="text-amber-500 flex-shrink-0 ml-0.5" />
          )}
          {isLimitedInViewport && (
            <span className="text-4xs font-bold text-sky-500 bg-sky-500/10 px-1.5 py-0.5 rounded-full ml-auto mr-1 flex-shrink-0 whitespace-nowrap">
              {t('layer_coverage.norway_only_short')}
            </span>
          )}
          {isPremium && !hasAccess && <Lock size={10} className={`text-amber-500 ${isLimitedInViewport ? '' : 'ml-auto'} mr-1 flex-shrink-0`} />}
          {isPremium && hasAccess && (
            <Star size={10} className={`text-emerald-500 ${isLimitedInViewport ? '' : 'ml-auto'} mr-1 flex-shrink-0 fill-emerald-500`} />
          )}
          {(count || 0) > 0 && isEnabled && (
            <span className={`text-3xs font-bold text-[var(--text-secondary)] bg-[var(--bg-surface-alt)] px-1.5 rounded-md ${isPremium ? 'mr-1' : 'ml-auto mr-1'} flex-shrink-0`}>
              {count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasError && (
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" title={t('layer_error.badge_title')} />
          )}
          {isLoading ? <LoadingSpinner /> : (
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={handleToggle}
              className="w-5 h-5 accent-emerald-500 cursor-pointer"
            />
          )}
          <button
            onClick={() => setDetailLayer(layerKey)}
            className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors p-2"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
    );
  };

  // --- LayerDetailView ---
  const LayerDetailView = ({ layerKey, onBack }: { layerKey: OverlayLayerKey; onBack: () => void }) => {
    const meta = LAYER_METADATA[layerKey];
    const Icon = meta.icon;
    const isEnabled = layers[layerKey];
    const hasAccess = hasLayerAccess(layerKey);
    const isPremium = !!meta.premiumFeatureKey;
    const isLimitedInViewport = viewportCountry && !isLayerSupportedInCountry(meta.countrySupport, viewportCountry);

    const handleToggle = () => {
      if (!hasAccess && meta.premiumFeatureKey) {
        showNudge(meta.premiumFeatureKey);
        return;
      }
      onUpdateLayers({ ...layers, [layerKey]: !isEnabled });
    };

    return (
      <div className="flex flex-col h-full animate-in slide-in-from-right-4 duration-200">
        {/* Back */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-2xs font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-3"
        >
          <ArrowLeft size={14} />
          <span>{t("auto.map.maplayerscontrol.tilbake", "Tilbake")}</span>
        </button>

        {/* Preview tile with sample markers */}
        <div className="w-full h-36 rounded-2xl overflow-hidden bg-[var(--bg-surface-alt)] mb-4 relative">
          <img
            src={meta.previewTileUrl}
            alt={meta.label}
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {/* Sample markers to illustrate the layer */}
          {[
            { top: '18%', left: '22%', opacity: 0.9, scale: 1 },
            { top: '45%', left: '60%', opacity: 0.85, scale: 0.9 },
            { top: '30%', left: '78%', opacity: 0.7, scale: 0.85 },
            { top: '65%', left: '35%', opacity: 0.75, scale: 0.85 },
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute flex items-center justify-center w-7 h-7 rounded-full bg-white/90 shadow-md backdrop-blur-sm"
              style={{ top: pos.top, left: pos.left, opacity: pos.opacity, transform: `scale(${pos.scale})` }}
            >
              {layerKey === 'osmAmenities' ? (
                <Tent size={13} className="text-emerald-500" />
              ) : (
                <Icon size={13} className={meta.iconColor} />
              )}
            </div>
          ))}
          {/* Layer icon badge */}
          <div className="absolute bottom-3 left-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center">
            <Icon size={18} className={meta.iconColor} />
          </div>
          {/* Premium access indicator */}
          {isPremium && (
            <div className={`absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center shadow-md ${
              hasAccess ? 'bg-emerald-500' : 'bg-amber-500'
            }`}>
              {hasAccess ? (
                <Star size={14} className="text-white fill-white" />
              ) : (
                <Lock size={14} className="text-white" />
              )}
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-black text-[var(--text-primary)] mb-1">{meta.label}</h3>

        {/* Premium badge */}
        {isPremium && !hasAccess && (
          <span className="inline-flex items-center gap-1 text-4xs font-black uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mb-3 w-fit">
            <Lock size={8} /> {meta.premiumFeatureKey === 'svv_traffic_data' ? 'Premium' : 'Supporter'}
          </span>
        )}
        {isPremium && hasAccess && (
          <span className="inline-flex items-center gap-1 text-4xs font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full mb-3 w-fit">
            <Star size={8} className="fill-emerald-500" /> {meta.premiumFeatureKey === 'svv_traffic_data' ? 'Premium' : 'Supporter'}
          </span>
        )}

        {/* Error banner */}
        {errorState?.[layerKey] && isEnabled && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-3">
            <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-2xs text-amber-700 dark:text-amber-400 leading-relaxed">
              {t('layer_error.source_unavailable')}
            </p>
          </div>
        )}

        {/* Coverage limitation banner */}
        {isLimitedInViewport && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-sky-500/10 border border-sky-500/20 mb-3">
            <Info size={13} className="text-sky-500 mt-0.5 flex-shrink-0" />
            <p className="text-2xs text-sky-700 dark:text-sky-400 leading-relaxed">
              {t('layer_coverage.not_available_here')}
            </p>
          </div>
        )}

        {/* Description */}
        <p className="text-tiny text-[var(--text-secondary)] leading-relaxed mb-4">
          {t(`layer_info.${layerKey}`)}
        </p>

        {/* National total count from stats feed */}
        {(() => {
          const statsKey = STATS_KEY_MAP[layerKey];
          if (!statsKey || !layerStats?.countries) return null;
          // Show counts for countries this layer supports
          const supported = meta.countrySupport.includes('*')
            ? Object.keys(layerStats.countries)
            : meta.countrySupport;
          const entries = supported
            .map(cc => {
              const c = layerStats.countries[cc];
              const count = c?.layers?.[statsKey]?.count;
              return count > 0 ? { label: c.label, count } : null;
            })
            .filter(Boolean) as { label: string; count: number }[];
          if (entries.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-2 mb-4">
              {entries.map(e => (
                <span key={e.label} className="inline-flex items-center gap-1.5 text-2xs font-bold text-[var(--text-secondary)] bg-[var(--bg-surface-alt)] px-2.5 py-1 rounded-lg">
                  <span className="text-[var(--text-primary)] tabular-nums">{e.count.toLocaleString('nb-NO')}</span>
                  <span className="text-[var(--text-muted)]">{t('layer_stats.in_country', { country: e.label })}</span>
                </span>
              ))}
            </div>
          );
        })()}

        {/* Toggle */}
        <div className="mt-auto pt-3 border-t border-[var(--border-primary)]">
          <button
            onClick={handleToggle}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-2xl transition-all ${
              isEnabled && hasAccess
                ? 'bg-emerald-50 border-2 border-emerald-500'
                : !hasAccess
                ? 'bg-[var(--bg-surface-alt)] border-2 border-[var(--border-secondary)] opacity-80'
                : 'bg-[var(--bg-surface-alt)] border-2 border-[var(--border-secondary)] hover:border-[var(--text-muted)]'
            }`}
          >
            <span className="text-tiny font-bold text-[var(--text-primary)]">
              {isEnabled && hasAccess ? 'Aktivert' : !hasAccess ? (meta.premiumFeatureKey === 'svv_traffic_data' ? 'Krever Premium' : 'Krever Supporter') : 'Deaktivert'}
            </span>
            <div className={`rounded-full transition-colors relative ${
              isEnabled && hasAccess ? 'bg-emerald-500' : 'bg-stone-300'
            }`} style={{ width: 40, height: 22 }}>
              <div className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${
                isEnabled && hasAccess ? 'translate-x-[20px]' : 'translate-x-[2px]'
              }`} />
            </div>
          </button>
        </div>
      </div>
    );
  };

  // Felles innhold (brukes av både mobil og desktop)
  const sharedContent = (
    <>
      {/* Zone 2: Scrollable content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 min-h-0">
        {activeTab === 'basemap' ? (
          <div className="space-y-3">
            <button
              onClick={() => {
                onUpdateLayers({ ...layers, satellite: false });
                if (is3DMode) onToggle3D?.();
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                !layers.satellite && !is3DMode
                  ? 'border-emerald-500 bg-emerald-50/50'
                  : 'border-[var(--border-secondary)] hover:border-[var(--text-muted)] bg-[var(--bg-surface)]'
              }`}
            >
              <img src={BASEMAP_THUMBNAILS.topo} alt="Terrengkart" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-[var(--bg-surface-alt)]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mountain size={14} className="text-amber-500 flex-shrink-0" />
                  <span className="text-[12px] font-bold text-[var(--text-primary)]">{t("auto.map.maplayerscontrol.terrengkart", "Terrengkart")}</span>
                </div>
                <p className="text-2xs text-[var(--text-secondary)] mt-0.5">{t("auto.map.maplayerscontrol.detaljert_topografisk_kart_fra", "Detaljert topografisk kart fra Kartverket")}</p>
              </div>
              {!layers.satellite && !is3DMode && (
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" />
                </div>
              )}
            </button>

            <button
              onClick={() => {
                if (!hasSatelliteAccess) { showNudge('satellite_map'); return; }
                onUpdateLayers({ ...layers, satellite: true });
                if (is3DMode) onToggle3D?.();
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                !hasSatelliteAccess ? 'opacity-80' : ''
              } ${
                layers.satellite && !is3DMode
                  ? 'border-emerald-500 bg-emerald-50/50'
                  : 'border-[var(--border-secondary)] hover:border-[var(--text-muted)] bg-[var(--bg-surface)]'
              }`}
            >
              <img src={BASEMAP_THUMBNAILS.satellite} alt="Flyfoto" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-[var(--bg-surface-alt)]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Eye size={14} className="text-blue-500 flex-shrink-0" />
                  <span className="text-[12px] font-bold text-[var(--text-primary)]">{t("auto.map.maplayerscontrol.flyfoto_hybrid", "Flyfoto (Hybrid)")}</span>
                  {!hasSatelliteAccess && <Lock size={10} className="text-amber-500" />}
                  {hasSatelliteAccess && <Star size={10} className="text-emerald-500 fill-emerald-500" />}
                </div>
                <p className="text-2xs text-[var(--text-secondary)] mt-0.5">{t("auto.map.maplayerscontrol.satellittbilder_med_stedsnavn", "Satellittbilder med stedsnavn")}</p>
              </div>
              {layers.satellite && !is3DMode && (
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" />
                </div>
              )}
            </button>

            {onToggle3D && (
              <button
                onClick={onToggle3D}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                  is3DMode
                    ? 'border-emerald-500 bg-emerald-50/50'
                    : 'border-[var(--border-secondary)] hover:border-[var(--text-muted)] bg-[var(--bg-surface)]'
                }`}
              >
                <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-[var(--bg-surface-alt)]">
                  <img src={BASEMAP_THUMBNAILS.topo} alt="3D" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-br from-stone-900/40 to-stone-900/10 flex items-center justify-center">
                    <span className="text-white font-black text-[15px] drop-shadow-md">3D</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Orbit size={14} className="text-indigo-500 flex-shrink-0" />
                    <span className="text-[12px] font-bold text-[var(--text-primary)]">3D terrengvisning</span>
                  </div>
                  <p className="text-2xs text-[var(--text-secondary)] mt-0.5">{t("auto.map.maplayerscontrol.interaktiv_3d_modell_med_hoyde", "Interaktiv 3D-modell med høydedata")}</p>
                </div>
                {is3DMode && (
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-white" />
                  </div>
                )}
              </button>
            )}

            <div className="mt-1 pt-3 border-t border-[var(--border-primary)]">
              <label className="flex items-center justify-between cursor-pointer group">
                <div className="flex items-center gap-3 text-tiny font-bold text-[var(--text-primary)] w-full">
                  <Mountain size={14} className="text-stone-500" />
                  <div className="flex flex-col">
                    <span>{t("auto.map.maplayerscontrol.terrengskygge_3d", "Terrengskygge (3D)")}</span>
                    <span className="text-3xs font-normal text-[var(--text-secondary)]">
                      Legger til skyggelegging
                      {viewportCountry === 'SE' && (
                        <span className="text-sky-500 ml-1">({t('layer_coverage.norway_only_short')})</span>
                      )}
                    </span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={layers.hillshade}
                  onChange={() => onUpdateLayers({ ...layers, hillshade: !layers.hillshade })}
                  className="w-5 h-5 accent-emerald-500"
                />
              </label>
            </div>
          </div>
        ) : detailLayer ? (
          <LayerDetailView layerKey={detailLayer} onBack={() => setDetailLayer(null)} />
        ) : (
          <div className="space-y-4">
            {/* Essential layers — always visible */}
            <div>
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">{t('map.layers.essential', 'Populære lag')}</p>
              {ESSENTIAL_LAYERS.filter(lk => {
                const meta = LAYER_METADATA[lk];
                if (meta?.hideOnIOS && isIOS) return false;
                if (viewportCountry && !isLayerSupportedInCountry(meta.countrySupport, viewportCountry)) return false;
                return true;
              }).map(lk => <LayerRow key={lk} layerKey={lk} />)}
            </div>

            {/* Active advanced layers — always visible so user can toggle them off */}
            {!showAdvanced && ADVANCED_SECTIONS.some(s => s.layers.some(lk => layers[lk as keyof typeof layers])) && (
              <div>
                <div className="h-px bg-[var(--border-primary)] my-2" />
                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">{t('map.layers.active_advanced', 'Aktive lag')}</p>
                {ADVANCED_SECTIONS.flatMap(s => s.layers).filter(lk => {
                  const meta = LAYER_METADATA[lk];
                  if (!layers[lk as keyof typeof layers]) return false;
                  if (meta?.hideOnIOS && isIOS) return false;
                  if (viewportCountry && !isLayerSupportedInCountry(meta.countrySupport, viewportCountry)) return false;
                  return true;
                }).map(lk => <LayerRow key={lk} layerKey={lk} />)}
              </div>
            )}

            {/* Toggle for advanced layers */}
            <button
              onClick={() => setShowAdvanced(v => !v)}
              className="flex items-center gap-2 text-2xs font-bold text-emerald-600 hover:text-emerald-500 transition-colors py-1"
            >
              {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showAdvanced ? t('map.layers.fewer_layers', 'Færre lag') : t('map.layers.more_layers', 'Vis flere lag')}
            </button>

            {/* Advanced sections — behind toggle */}
            {showAdvanced && ADVANCED_SECTIONS.map((section, sIdx) => {
              const visibleLayers = section.layers.filter(lk => {
                const meta = LAYER_METADATA[lk];
                if (meta?.hideOnIOS && isIOS) return false;
                if (viewportCountry && !isLayerSupportedInCountry(meta.countrySupport, viewportCountry)) return false;
                return true;
              });
              if (visibleLayers.length === 0) return null;
              return (
                <React.Fragment key={section.titleKey}>
                  {sIdx > 0 && <div className="h-px bg-[var(--border-primary)] my-2" />}
                  <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">{section.title}</p>
                  {visibleLayers.map(lk => <LayerRow key={lk} layerKey={lk} />)}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Vehicle filter — inside scroll area */}
        <div className="mt-4 pt-3 border-t border-[var(--border-primary)]">
          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-3 text-tiny font-bold text-[var(--text-primary)]">
              <Route size={14} className="text-amber-500" /> <span>{t("auto.map.maplayerscontrol.vis_alle_veityper", "Vis alle veityper")}</span>
            </div>
            <input
              type="checkbox"
              checked={layers.vehicleFiltered}
              onChange={() => onUpdateLayers({ ...layers, vehicleFiltered: !layers.vehicleFiltered })}
              className="w-5 h-5 accent-amber-500"
            />
          </label>
          {layers.vehicleFiltered && (
            <p className="text-3xs text-[var(--text-muted)] mt-1 ml-[26px]">{t("auto.map.maplayerscontrol.viser_plasser_skjult_av_kjoret", "Viser plasser skjult av kjøretøyinnstillinger")}</p>
          )}
          {!layers.vehicleFiltered && activeVehicleLength && activeVehicleLength > 700 && (
            <p className="text-3xs text-amber-400/80 mt-1 ml-[26px]">
              {t('map.layers.vehicle_length_active', { length: (activeVehicleLength / 100).toFixed(1) })}
            </p>
          )}
        </div>

        {/* iOS-spesifikk melding om utilgjengelige lag */}
        {isIOS && (
          <p className="text-3xs text-[var(--text-muted)] mt-3 pt-2 border-t border-[var(--border-primary)] leading-relaxed">
            {t("auto.map.maplayerscontrol.noen_kartlag_eiendomsgrenser_t", "Noen kartlag (eiendomsgrenser, tømmeplasser, nedbørsradar) er ikke tilgjengelig på iOS grunnet tekniske begrensninger i WKWebView.")}
          </p>
        )}

        {/* Offline download — inside scroll area, hidden on desktop, auto-hides after 4s */}
        {showOfflineBtn && (
          <div className={`mt-3 pt-3 border-t border-[var(--border-primary)] pb-[calc(var(--safe-area-bottom,0px)+0.5rem)] ${Capacitor.isNativePlatform() ? '' : 'md:hidden'}`}>
            <button
              onClick={() => {
                if (!isPaidUser) { showNudge('offline_maps'); setShowMenu(false); return; }
                onOpenOfflineManager?.();
                setShowMenu(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-tiny font-bold text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-colors ${!isPaidUser ? 'opacity-80' : ''}`}
            >
              <Download size={14} className="text-emerald-600 flex-shrink-0" />
              <span>{t("auto.map.maplayerscontrol.nedlast_kart_offline", "Nedlast kart (offline)")}</span>
              {!isPaidUser && <Lock size={10} className="text-amber-500 ml-auto" />}
            </button>
          </div>
        )}
      </div>
    </>
  );

  const tabButtons = (mobile: boolean) => (
    <>
      <button
        onClick={() => { setActiveTab('basemap'); setDetailLayer(null); }}
        className={mobile
          ? `flex-1 py-2 rounded-lg text-tiny font-bold transition-all ${activeTab === 'basemap' ? 'bg-[var(--bg-overlay)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-muted)]'}`
          : `text-3xs font-black uppercase tracking-[0.15em] transition-all pb-2.5 whitespace-nowrap ${activeTab === 'basemap' ? 'text-[var(--text-primary)] border-b-2 border-emerald-500' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] border-b-2 border-transparent'}`
        }
      >
        {t("auto.map.maplayerscontrol.bakgrunnskart", "Bakgrunnskart")}
      </button>
      <button
        onClick={() => { setActiveTab('overlays'); setDetailLayer(null); }}
        className={mobile
          ? `flex-1 py-2 rounded-lg text-tiny font-bold transition-all ${activeTab === 'overlays' ? 'bg-[var(--bg-overlay)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-muted)]'}`
          : `text-3xs font-black uppercase tracking-[0.15em] transition-all pb-2.5 whitespace-nowrap ${activeTab === 'overlays' ? 'text-[var(--text-primary)] border-b-2 border-emerald-500' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] border-b-2 border-transparent'}`
        }
      >
        {t("auto.map.maplayerscontrol.kartlag", "Kartlag")}
      </button>
    </>
  );

  return (
    <div className="relative md:static">
      <button
        onClick={() => {
          onDismissLayerTip?.();
          setShowMenu(!showMenu);
        }}
        aria-label={t("auto.map.maplayerscontrol.tilpass_kart", "Tilpass kart")}
        aria-expanded={showMenu}
        className={`w-11 h-11 rounded-xl bg-drab border border-drab-line shadow-xl flex items-center justify-center transition-all ${showMenu ? 'text-accent' : 'text-[var(--text-secondary)]'} hover:scale-105`}
      >
        <Layers size={14} className="md:hidden" /><Layers size={18} className="hidden md:block" />
      </button>

      {showLayerTip && !showMenu && (
        <div className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-2xl border border-emerald-500/25 bg-[var(--bg-overlay)]/95 px-3 py-2.5 text-tiny font-bold leading-relaxed text-[var(--text-primary)] shadow-2xl backdrop-blur-xl z-[5000] animate-in fade-in slide-in-from-top-1 md:fixed md:right-16 md:top-[calc(var(--safe-area-top,0px)+82px)] md:mt-0">
          <div className="absolute -top-1.5 right-3 h-3 w-3 rotate-45 border-l border-t border-emerald-500/25 bg-[var(--bg-overlay)]/95 md:right-4" />
          <p className="relative">
            {t('map.layers.tip_bubble', 'Usikker på ikonene i kartet? Trykk på kartlag-knappen for å slå punkter av/på og se forklaring på hvert kartlag.')}
          </p>
        </div>
      )}

      {/* Desktop: fixed dropdown anchored to top-right, just under the header */}
      {showMenu && (
        <div className="hidden md:flex flex-col fixed right-16 bg-[var(--bg-overlay)] border border-[var(--border-secondary)] rounded-3xl shadow-2xl w-72 max-w-[calc(100vw-3rem)] animate-in slide-in-from-top-2 z-[5000]" style={{ top: 'calc(var(--safe-area-top, 0px) + 90px)', maxHeight: 'calc(100vh - var(--safe-area-top, 0px) - 110px)' }}>
          <div className="px-5 pt-5 pb-0 flex-shrink-0">
            <div className="flex items-center justify-between -mt-1 mb-3">
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t("auto.map.maplayerscontrol.tilpass_kart", "Tilpass kart")}</p>
              <button onClick={() => setShowMenu(false)} aria-label={t("auto.map.maplayerscontrol.aria-label.lukk", "Lukk")} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="flex gap-4 border-b border-[var(--border-secondary)]">
              {tabButtons(false)}
            </div>
          </div>
          {sharedContent}
        </div>
      )}

      {/* Mobil: portal til document.body — dekker headeren */}
      {showMenu && createPortal(
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-[9000]" onClick={() => setShowMenu(false)} />
          <div className="md:hidden fixed inset-0 z-[9001] flex flex-col bg-[var(--bg-base)] animate-in slide-in-from-bottom-4">
            <div className="px-5 pt-[calc(var(--safe-area-top,0px)+1.25rem)] pb-0 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t("auto.map.maplayerscontrol.tilpass_kart", "Tilpass kart")}</p>
                <button onClick={() => setShowMenu(false)} aria-label={t("auto.map.maplayerscontrol.aria-label.lukk", "Lukk")} className="w-11 h-11 flex items-center justify-center rounded-full bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                  <X size={16} />
                </button>
              </div>
              <div className="flex bg-[var(--bg-surface-alt)] rounded-xl p-1 mb-1">
                {tabButtons(true)}
              </div>
            </div>
            {sharedContent}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default MapLayersControl;
